import { Component, inject, input } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { ConsigneeViewActionsService } from '@app/shared/services/view-configs/consignee-view-actions.service';
import { ColumnConfig } from '@app/shared/services/view-configs/column-creator';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { StorageService } from '@app/core/services/client-services/storage-service/storage.service';
import { AppStore } from '@app/stores/appStore';
import { UserRoles } from 'api/models';
import { CurrentSiteRole } from '@app/shared/models/authentication/session';

@Component({
    selector: 'app-notification-actions',
    standalone: true,
    imports: [
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        TranslateModule,
    ],
    template: `
        <div class="tw-flex tw-items-center tw-justify-start">
            <!-- Download Button -->
            <div class="place">
                <button
                    mat-icon-button
                    [matTooltip]="
                        'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.DOWNLOAD-BUNDLE'
                            | translate
                    "
                    (click)="onDownload()">
                    <mat-icon fontSet="material-symbols-outlined"
                        >file_download</mat-icon
                    >
                </button>
            </div>

            <!-- View Button -->
            <div class="place">
                <button
                    mat-icon-button
                    [matTooltip]="
                        'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.VIEW-DELIVERY-NOTE'
                            | translate
                    "
                    (click)="onView()">
                    <mat-icon fontSet="material-symbols-outlined"
                        >picture_as_pdf</mat-icon
                    >
                </button>
            </div>
        </div>
    `,
    styles: [
        `
            .place {
                margin-left: 4px;
            }
        `,
    ],
})
export class NotificationActionsComponent {
    row = input<any>(); // The delivery note row with bundleKey, bundleNumber, etc.
    parentRow = input<any>(); // The notification event row
    column = input<ColumnConfig>(); // Column configuration (automatically passed by table framework)

    private actionsService = inject(ConsigneeViewActionsService);
    private appStore = inject(AppStore);
    private sessionService = inject(SessionService);
    private storageService = inject(StorageService);

    onView(): void {
        if (!this.row()?.bundleKey) return;

        this.switchToNotificationLocation();
        const bundleData = this.createBundleObject();
        this.actionsService.viewFolderAsPdf(bundleData);
    }

    onDownload(): void {
        if (!this.row()?.bundleKey) return;

        this.switchToNotificationLocation();
        const bundleData = this.createBundleObject();
        this.actionsService.downloadDeliveryBundle(bundleData);
    }

    /**
     * Switches to the notification's organization site if it differs from the current site.
     * Uses individual store setters to avoid triggering data refresh.
     */
    private switchToNotificationLocation(): void {
        const notificationOrgSiteKey =
            this.row().parentOrgSiteKey || this.parentRow()?.organizationSiteKey;

        const currentOrgSiteKey = this.appStore.currentOrganizationSiteKey();

        // Only switch if notification is from a different location
        if (
            notificationOrgSiteKey &&
            notificationOrgSiteKey !== currentOrgSiteKey
        ) {
            const session = this.sessionService.getSession();
            const targetSite = session.organizationSites.find(
                site => site._key === notificationOrgSiteKey
            );

            if (targetSite) {
                // Use same role determination logic as user-profile.component.ts
                let role = 'consignee';
                if (targetSite.roles.includes(UserRoles.RemoteConsignee)) {
                    role = 'remote_consignee';
                }

                const siteRole: CurrentSiteRole = {
                    role: role,
                    siteInfo: targetSite,
                };

                // Use INDIVIDUAL setters (no data refresh) to avoid table reload
                this.appStore.setCurrentOrganizationSite(targetSite);
                this.appStore.setCurrentSiteRole(siteRole);

                // Update session service observables (triggers header update)
                this.sessionService.currentSiteRole.set(siteRole);
                this.sessionService.setCurrentOrganizationSite(targetSite._key);

                // Persist to localStorage
                this.storageService.setItem('currentSiteRole', siteRole);
            }
        }
    }

    private createBundleObject(): any {
        const orgSiteKey =
            this.row().parentOrgSiteKey || this.parentRow()?.organizationSiteKey;

        return {
            _key: this.row().bundleKey,
            bundleNumber: this.row().bundleNumber,
            consigneeKey: orgSiteKey,
            accessSiteKey: orgSiteKey,
            // Required properties with default values
            accessToken: '',
            carrierKey: '',
            carrierName: '',
            consigneeLoadCarrierExchange: false,
            consigneeName: '',
            consignorKey: '',
            consignorLoadCarrierExchange: false,
            consignorName: '',
            dateCreated: Date.now(),
            dateShipped: Date.now(),
            discrepancies: false,
            name: this.row().bundleNumber,
            numberOfDeliveryNotes: 1,
            palletTickets: false,
            status: 'sent' as any,
        };
    }
}
